import { Box, Stack, Typography, Button } from '@mui/material';

import { Accordion } from '@/components/shared/Accordion';
import { GenericBarChart } from '@/components/shared/GenericBarChart';
import { scrollDesign } from '@/components/Analysis/Insights/InsightsExplorer/styles';

import { abyssColors } from '@/theme/colors';
import { ArrowUpward } from '@mui/icons-material';
import { AbyssIcon } from '@/assets/icons/AbyssIcon';

type Props = {
  summedMetrics: Record<string, string | number>[];
  metricsByPlatform: Record<string, string | number>[];
  legendsData: Record<string, string>;
  totalRiskScore?: string;
  criticalityChartTitle?: string;
  platformChartTitle?: string;
  sortBy?: 'high-to-low' | 'low-to-high';
  showCriticalityChart?: boolean;
  isCoatingFilter?: boolean;
  onConfigureClick?: () => void;
};

export const PlatformOverview = ({
  summedMetrics,
  metricsByPlatform,
  legendsData,
  totalRiskScore,
  criticalityChartTitle = 'Alarm Level 3s by Criticality',
  platformChartTitle = 'Alarm Level 3s by platform',
  sortBy,
  showCriticalityChart,
  isCoatingFilter = false,
  onConfigureClick,
}: Props) => {
  return (
    <Accordion
      title="Overview"
      isEditing={false}
      sx={{ backgroundColor: abyssColors.primary[50], pb: 1 }}
      data-testid="platform-overview-accordion"
      action={
        onConfigureClick && (
          <Button
            variant="outlined"
            size="small"
            startIcon={<AbyssIcon iconName="gear" style={{ height: '1.6rem', width: '1.6rem' }} />}
            onClick={(event) => {
              event.stopPropagation();
              onConfigureClick();
            }}
            sx={{
              color: abyssColors.primary[500],
              borderColor: abyssColors.primary[300],
              '&:hover': {
                borderColor: abyssColors.primary[500],
                backgroundColor: abyssColors.primary[50],
              },
              minWidth: '130px',
            }}
          >
            Configure
          </Button>
        )
      }
    >
      <Stack direction="row" spacing={2} px={2}>
        {totalRiskScore && (
          <Box
            sx={{
              width: '20%',
              border: `1px solid ${abyssColors.primary[100]}`,
              borderRadius: 2,
              backgroundColor: 'white',
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              position: 'relative',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: abyssColors.primary[600],
                fontWeight: 500,
                mb: 2,
              }}
            >
              Total risk score
            </Typography>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                flex: 1,
              }}
            >
              <Typography
                sx={{
                  fontSize: '5rem',
                  fontWeight: 500,
                  color: abyssColors.primary[800],
                  mb: 2,
                }}
              >
                {totalRiskScore}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <ArrowUpward sx={{ fontSize: '2rem', color: 'red' }} />
                <Typography variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                  {15}%
                </Typography>
                <Typography variant="body2"> increase since last year</Typography>
              </Box>
            </Box>
          </Box>
        )}

        {showCriticalityChart && (
          <Box
            sx={{
              width: totalRiskScore ? '20%' : '30%',
              ...scrollDesign,
            }}
          >
            <GenericBarChart
              title={criticalityChartTitle}
              data={summedMetrics}
              legendData={legendsData}
              hideLegend
              containerWidth={10}
              removeZeroValueRecords
              barSize={30}
            />
          </Box>
        )}
        <Box
          sx={{
            width: !showCriticalityChart ? '100%' : totalRiskScore ? '60%' : '70%',
            ...scrollDesign,
          }}
        >
          <GenericBarChart
            title={platformChartTitle}
            data={metricsByPlatform}
            legendData={legendsData}
            containerWidth={10}
            removeZeroValueRecords
            isCoatingFilter={isCoatingFilter}
            barSize={30}
            sortBy={sortBy}
          />
        </Box>
      </Stack>
    </Accordion>
  );
};
